import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { StepShell } from "../components/StepShell"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"

// Seed local state from any previously-saved answers so the step is
// resume-friendly. Keys are driven by the server-provided question list
// (peerQuestions), so wording lives in one place (IntakeReport).
function seedOpen(report, questions) {
  const saved = {}
  ;(report?.prospectAnswers?.open_ended || []).forEach((a) => {
    if (a) saved[a.key] = a.answer || ""
  })
  return Object.fromEntries(questions.map((q) => [q.key, saved[q.key] || ""]))
}

function seedYesNo(report, questions) {
  const saved = {}
  ;(report?.prospectAnswers?.yes_no || []).forEach((a) => {
    if (a) saved[a.key] = a.answer
  })
  return Object.fromEntries(questions.map((q) => [q.key, saved[q.key] || null]))
}

export function PeerQuestions({ report, peerQuestions }) {
  const errors = usePage().props.errors || {}
  const openQuestions = peerQuestions?.openEnded || []
  const yesNoQuestions = peerQuestions?.yesNo || []

  const [open, setOpen] = useState(() => seedOpen(report, openQuestions))
  const [yesNo, setYesNo] = useState(() => seedYesNo(report, yesNoQuestions))
  const [processing, setProcessing] = useState(false)

  const submit = () => {
    setProcessing(true)
    // Inertia router.* (not bare fetch) so the global reject_non_html_formats
    // filter doesn't 406 the request.
    router.patch(
      "/app/plan/intake",
      { step: "peer_questions", open_ended: open, yes_no: yesNo, advance: "1" },
      { preserveScroll: true, onFinish: () => setProcessing(false) },
    )
  }

  const allOpenAnswered = openQuestions
    .filter((q) => !q.optional)
    .every((q) => (open[q.key] || "").trim().length > 0)
  const allYesNoAnswered = yesNoQuestions.every((q) => yesNo[q.key] === "yes" || yesNo[q.key] === "no")
  const canContinue = allOpenAnswered && allYesNoAnswered

  return (
    <StepShell
      title="A few questions"
      subtitle="Your answers shape a personalized report on how a peer group could help you. Be as honest as you'd like — there are no wrong answers."
      onNext={submit}
      nextDisabled={!canContinue}
      processing={processing}
    >
      <div className="space-y-8">
        {openQuestions.map((q, idx) => (
          <div key={q.key}>
            <label htmlFor={q.key} className="block font-medium text-zinc-900">
              <span className="text-kf-red mr-1.5">{idx + 1}.</span>
              {q.prompt}
              {q.optional && (
                <span className="ml-2 text-sm font-normal text-zinc-400">(Optional)</span>
              )}
            </label>
            {q.helper && (
              <p className="mt-1.5 mb-2 rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm text-zinc-600">
                {q.helper}
              </p>
            )}
            {q.optional && !q.helper && (
              <p className="mt-1.5 mb-2 text-sm text-zinc-500">
                Share whatever you'd like, or leave this blank.
              </p>
            )}
            {/* NOTE: a privacy disclosure (the prayer answer is visible to the
                inviting coach in the report) is pending a product decision. */}
            <Textarea
              id={q.key}
              rows={5}
              maxLength={5000}
              value={open[q.key] || ""}
              onChange={(e) => setOpen((s) => ({ ...s, [q.key]: e.target.value }))}
              placeholder="Share as much or as little as you'd like…"
              className={errors[q.key] ? "border-red-400 focus-visible:ring-red-400" : ""}
            />
            {errors[q.key] && (
              <p className="text-sm text-red-600 mt-1">{errors[q.key]}</p>
            )}
          </div>
        ))}

        <div className="space-y-5 pt-6 border-t border-zinc-100">
          {yesNoQuestions.map((q, idx) => (
            <div key={q.key}>
              <p className="font-medium text-zinc-900 mb-2">
                <span className="text-kf-red mr-1.5">{openQuestions.length + idx + 1}.</span>
                {q.prompt}
              </p>
              <div className="flex gap-2">
                {["yes", "no"].map((val) => (
                  <Button
                    key={val}
                    type="button"
                    variant={yesNo[q.key] === val ? "gradient" : "outline"}
                    size="sm"
                    onClick={() => setYesNo((s) => ({ ...s, [q.key]: val }))}
                    aria-pressed={yesNo[q.key] === val}
                  >
                    {val === "yes" ? "Yes" : "No"}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </StepShell>
  )
}
